import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import { Search, Filter, X as XIcon } from 'lucide-react';
import './Admin.css';

interface User {
  _id: string;
  username: string;
  email: string;
  role: 'super_admin' | 'admin' | 'staff' | 'viewer';
  permissions: {
    dashboard: boolean;
    accounts: boolean;
    groups: boolean;
    analytics: boolean;
    members: boolean;
    admin: boolean;
  };
  isActive: boolean;
  createdAt: string;
  createdBy?: {
    username: string;
    email: string;
  };
}

interface RoleInfo {
  roles: string[];
  descriptions: Record<string, string>;
}

interface PermissionInfo {
  pages: string[];
  descriptions: Record<string, string>;
}

const Admin: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<RoleInfo | null>(null);
  const [permissions, setPermissions] = useState<PermissionInfo | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'viewer' as 'super_admin' | 'admin' | 'staff' | 'viewer',
    permissions: {
      dashboard: false,
      accounts: false,
      groups: false,
      analytics: false,
      members: false,
      admin: false,
    },
    isActive: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, permissionsRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getRoles(),
        adminAPI.getPermissions(),
      ]);

      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setPermissions(permissionsRes.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        permissions: { ...user.permissions },
        isActive: user.isActive,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'viewer',
        permissions: {
          dashboard: false,
          accounts: false,
          groups: false,
          analytics: false,
          members: false,
          admin: false,
        },
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        const updateData: any = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          permissions: formData.permissions,
          isActive: formData.isActive,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await adminAPI.updateUser(editingUser._id, updateData);
      } else {
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        await adminAPI.createUser(formData);
      }
      await fetchData();
      handleCloseModal();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // Search filter
      const matchesSearch =
        searchQuery === '' ||
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase());

      // Role filter
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.isActive) ||
        (statusFilter === 'inactive' && !user.isActive);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || roleFilter !== 'all' || statusFilter !== 'all';

  const togglePermission = (permission: string) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission as keyof typeof formData.permissions],
      },
    });
  };

  const handleRoleChange = (role: string) => {
    setFormData({
      ...formData,
      role: role as typeof formData.role,
      // Auto-set permissions based on role
      permissions: {
        dashboard: role !== 'viewer',
        accounts: role === 'admin' || role === 'super_admin',
        groups: role === 'admin' || role === 'super_admin',
        analytics: role !== 'viewer',
        members: role === 'admin' || role === 'super_admin',
        admin: role === 'super_admin',
      },
    });
  };

  if (loading && users.length === 0) {
    return <Loader fullScreen text="Loading admin panel..." />;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>User Management</h1>
        {currentUser?.role === 'super_admin' && (
          <button className="btn-primary" onClick={() => handleOpenModal()}>
            + Add User
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.isActive).length}</div>
          <div className="stat-label">Active Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => !u.isActive).length}</div>
          <div className="stat-label">Inactive Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{filteredUsers.length}</div>
          <div className="stat-label">Filtered Results</div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-filter-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="clear-search"
                aria-label="Clear search"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="filter-item">
            <select
              id="role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <div className="filter-item">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="clear-filters-btn">
              <XIcon className="h-4 w-4" />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="table-header">
        <div className="table-title">
          <h2>Users</h2>
          <span className="table-count">
            Showing {filteredUsers.length} of {users.length} users
          </span>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-results">
                  <div className="no-results-content">
                    <p>No users found matching your criteria.</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="btn-secondary">
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
              <tr key={user._id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role}`}>
                    {user.role.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  <div className="permissions-list">
                    {Object.entries(user.permissions)
                      .filter(([_, value]) => value)
                      .map(([key]) => (
                        <span key={key} className="permission-badge">
                          {key}
                        </span>
                      ))}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    {currentUser?.role === 'super_admin' && (
                      <>
                        <button
                          className="btn-edit"
                          onClick={() => handleOpenModal(user)}
                        >
                          Edit
                        </button>
                        {user._id !== currentUser?.id && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(user._id)}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!editingUser}
                  minLength={6}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    required
                  >
                    {roles?.roles.map((role) => (
                      <option key={role} value={role}>
                        {role.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                  {roles && (
                    <small className="role-description">
                      {roles.descriptions[formData.role]}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === 'active',
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Page Permissions</label>
                <div className="permissions-grid">
                  {permissions?.pages.map((page) => {
                    const isSuperAdmin = formData.role === 'super_admin';
                    const isAdminPage = page === 'admin';
                    const isDisabled = isSuperAdmin || (isAdminPage && !isSuperAdmin);
                    
                    return (
                      <label key={page} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.permissions[page as keyof typeof formData.permissions]}
                          onChange={() => togglePermission(page)}
                          disabled={isDisabled}
                        />
                        <span>
                          <strong>{page}</strong>
                          <small>{permissions.descriptions[page]}</small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {error && <div className="form-error">{error}</div>}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;

